package com.swetdot.swetgram.ui.theme

import androidx.compose.material3.Typography
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.sp

/**
 * The SDS §5.3 type scale, mapped onto Material3's slots.
 *
 * All ten SDS roles with their exact sizes, line heights and weights. The mapping is chosen so
 * the Material components we do NOT rewrite land on the right style by default: `TopAppBar`
 * reads `titleLarge`, `Button` reads `labelLarge`, list supporting text reads `bodyMedium`.
 *
 * The previous scale matched the SDS on none of the ten. Two of the differences are worth
 * naming because they are easy to reintroduce:
 *
 *  - `bodyLarge` is 17sp, not Material's 16sp. SDS §5.4 puts message text here and calls 17sp
 *    the readability floor for chat. Anything wanting ordinary body text wants `bodyMedium`.
 *  - Tab labels are `label.large` (15sp/600), but `NavigationBarItem` styles its label from
 *    `labelMedium`. A bare Text in a nav item therefore ships at 13sp unless the style is
 *    passed explicitly -- and it looks deliberate, so nobody notices.
 *
 * No `fontFamily` is set. The SDS asks for Inter with a system fallback; until the font is
 * actually bundled, naming it would resolve to the system face anyway. The fallback is the
 * honest default rather than a family that is not there.
 */
val SwetgramTypography = Typography(
    // display.large -- marketing screens, major empty states
    displayLarge = TextStyle(fontSize = 36.sp, lineHeight = 44.sp, fontWeight = FontWeight.Bold),
    // title.large -- main screen title
    headlineLarge = TextStyle(fontSize = 28.sp, lineHeight = 36.sp, fontWeight = FontWeight.Bold),
    // title.medium -- auth title, section hero
    headlineMedium = TextStyle(fontSize = 24.sp, lineHeight = 32.sp, fontWeight = FontWeight.Bold),
    // headline -- screen headers, card titles
    headlineSmall = TextStyle(fontSize = 20.sp, lineHeight = 28.sp, fontWeight = FontWeight.SemiBold),
    // Also `headline`: this is the slot TopAppBar reads.
    titleLarge = TextStyle(fontSize = 20.sp, lineHeight = 28.sp, fontWeight = FontWeight.SemiBold),
    // Chat row title -- body.medium at 600 (SDS §5.4)
    titleMedium = TextStyle(fontSize = 16.sp, lineHeight = 24.sp, fontWeight = FontWeight.SemiBold),
    titleSmall = TextStyle(fontSize = 14.sp, lineHeight = 20.sp, fontWeight = FontWeight.SemiBold),
    // body.large -- chat messages, main text
    bodyLarge = TextStyle(fontSize = 17.sp, lineHeight = 25.sp, fontWeight = FontWeight.Normal),
    // body.medium -- default body text
    bodyMedium = TextStyle(fontSize = 16.sp, lineHeight = 24.sp, fontWeight = FontWeight.Normal),
    // body.small -- secondary copy, chat row preview
    bodySmall = TextStyle(fontSize = 14.sp, lineHeight = 20.sp, fontWeight = FontWeight.Normal),
    // label.large -- buttons, tabs
    labelLarge = TextStyle(fontSize = 15.sp, lineHeight = 20.sp, fontWeight = FontWeight.SemiBold),
    // label.medium -- metadata, chips
    labelMedium = TextStyle(fontSize = 13.sp, lineHeight = 18.sp, fontWeight = FontWeight.Medium),
    // caption -- timestamps, counters, helper text
    labelSmall = TextStyle(fontSize = 12.sp, lineHeight = 16.sp, fontWeight = FontWeight.Normal),
)

/**
 * `caption.small` -- 10sp. AN ELEVENTH ROLE THAT THE SDS DOES NOT HAVE, and it is an EXTENSION of
 * §5.3 rather than a reading of it. The scale stops at `caption` 12sp; this is a rung below the
 * bottom of it, added for one thing and recorded in DECISIONS.md §25 beside the other overrides.
 *
 * WHAT IT IS FOR. The conversation's metadata -- the timestamp, "edited", and the delivery tick
 * beside them -- now sits INSIDE the bubble, trailing the message text on the same line, and the
 * user asked for it "2x smaller". It cannot live in a Material `Typography` slot because all ten of
 * those are already spoken for by §5.3's ten roles, so it is a style of its own next to them rather
 * than a size written at the call site.
 *
 * WHY 10sp AND NOT 6. "2x smaller" than `caption` is literally 6sp, and 6sp is not a size -- it is
 * below every platform's smallest system text (iOS caption2 is 11sp, Material's smallest role is
 * 11sp), it is unreadable at arm's length, and it would make the one element that carries delivery
 * state the least legible thing on the screen. In the mockup the timestamp glyphs measure roughly
 * 60% of the message text's height; message text is `bodyMedium` 16sp, which puts the timestamp near
 * 9.6sp, and 10sp is that rounded to a whole point. It is a REDUCTION OF A THIRD from 12sp, not a
 * halving, and the difference between the two numbers is the whole of this note.
 *
 * IT IS sp AND NOT dp, which is what keeps the choice defensible: a reader who has turned the system
 * font up to 200% sees this at 20sp, larger than the message text is at 100%. A dp value would have
 * frozen 10 into a number nobody could grow.
 *
 * The 13sp line height keeps §5.3's own ratio -- the scale runs at about 1.3x the size at the small
 * end (12/16, 13/18, 15/20) -- so a metadata line stacks with the same rhythm as everything else.
 */
val SwetgramCaptionSmall: TextStyle =
    TextStyle(fontSize = 10.sp, lineHeight = 13.sp, fontWeight = FontWeight.Normal)
